import React, { useState } from 'react';
import { useHistory } from 'react-router-dom';
import classNames from 'classnames';

import { useRouteConfiguration } from '../../context/routeConfigurationContext';
import { FormattedMessage, useIntl } from '../../util/reactIntl';
import { createResourceLocatorString } from '../../util/routes';
import { configForListing, subcategoryRefOfListing } from '../../util/data';
import useCategoryConfigMap from '../../hooks/useCategoryConfigMap';
import { onCreateEmergencyLead } from '../../util/api';
import { IconSpinner } from '../../components';

import css from './EmergencyServiceCta.module.css';

/**
 * Badge + one-click lead capture for listings whose category has
 * emergencyEnabled on (admin-configured, see ConfigureCategoryDialog).
 *
 * The emergency-service workflow itself (matching, dispatch, merchant
 * notification) doesn't exist yet -- clicking this only records the
 * customer as interested (emergency_service_leads, read in now-webadmin)
 * and tells them it's coming soon. Resolved at render time from the
 * category's currently active config, same pattern as TransactionModelBadge,
 * so an admin toggle takes effect on next load with no listing rewrite.
 */
const EmergencyServiceCta = props => {
  const { listing, currentUser, isAuthenticated } = props;
  const intl = useIntl();
  const history = useHistory();
  const routeConfiguration = useRouteConfiguration();
  const [status, setStatus] = useState('idle'); // idle | submitting | submitted | error

  const publicData = listing?.attributes?.publicData || {};
  const categoryConfigMap = useCategoryConfigMap();
  const listingConfig = configForListing(categoryConfigMap, publicData);

  if (!listingConfig?.emergencyEnabled) {
    return null;
  }

  const handleClick = async () => {
    console.log('[EmergencyServiceCta] Click', {
      isAuthenticated,
      status,
      listingId: listing?.id?.uuid,
    });

    if (!isAuthenticated) {
      const listingUrl = createResourceLocatorString(
        'ListingPage',
        routeConfiguration,
        { id: listing?.id?.uuid, slug: publicData?.slug || 'listing' },
        {}
      );
      history.push(createResourceLocatorString('LoginPage', routeConfiguration, {}, {}), {
        from: listingUrl,
      });
      return;
    }

    if (status === 'submitting' || status === 'submitted') {
      return;
    }

    setStatus('submitting');

    const { firstName, lastName, displayName, protectedData: userProtectedData } =
      currentUser?.attributes?.profile || {};
    const { callingCode, phoneNumber } = userProtectedData?.mobile_phone_number || {};
    const customerPhone = callingCode && phoneNumber ? `${callingCode}${phoneNumber}` : phoneNumber || null;

    const payload = {
      customerId: currentUser?.id?.uuid,
      customerName: displayName || [firstName, lastName].filter(Boolean).join(' ') || null,
      customerEmail: currentUser?.attributes?.email || null,
      customerPhone,
      listingId: listing?.id?.uuid,
      listingTitle: listing?.attributes?.title || null,
      merchantId: listing?.author?.id?.uuid || null,
      categorySlug: subcategoryRefOfListing(publicData),
      parentCategorySlug: publicData?.categoryLevel1 ? String(publicData.categoryLevel1) : null,
    };
    console.log('[EmergencyServiceCta] Submitting lead', payload);

    try {
      const response = await onCreateEmergencyLead(payload);
      console.log('[EmergencyServiceCta] Lead captured', response);
      setStatus('submitted');
    } catch (error) {
      console.error(
        '[EmergencyServiceCta] Failed to capture lead',
        error?.status,
        error?.data || error?.message || error
      );
      setStatus('error');
    }
  };

  if (status === 'submitted') {
    return (
      <div className={classNames(css.root, css.rootSubmitted)}>
        <div className={css.header}>
          <span className={css.badge}>
            <FormattedMessage id="EmergencyServiceCta.badge" />
          </span>
        </div>
        <div className={css.submittedRow}>
          <span className={css.submittedIcon}>&#10003;</span>
          <p className={css.submittedText}>
            <FormattedMessage id="EmergencyServiceCta.submitted" />
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className={css.root}>
      <div className={css.header}>
        <span className={css.badge}>
          <FormattedMessage id="EmergencyServiceCta.badge" />
        </span>
      </div>
      <p className={css.description}>
        <FormattedMessage id="EmergencyServiceCta.description" />
      </p>
      <button
        type="button"
        className={css.ctaButton}
        onClick={handleClick}
        disabled={status === 'submitting'}
      >
        {status === 'submitting' ? (
          <>
            <IconSpinner className={css.spinner} />
            <FormattedMessage id="EmergencyServiceCta.submitting" />
          </>
        ) : (
          <FormattedMessage id="EmergencyServiceCta.cta" />
        )}
      </button>
      {status === 'error' ? (
        <p className={css.errorText}>
          <FormattedMessage id="EmergencyServiceCta.error" />
        </p>
      ) : null}
    </div>
  );
};

export default EmergencyServiceCta;
